import type { ScreenerVolumeDataPoint } from '@/types'
import { FEATURE_SCREENER_TRADES_ENABLED } from '@/lib/featureFlags'

const INTERVAL_SECONDS: Record<string, number> = { '5s': 5, '1m': 60, '5m': 300, '30m': 1800, '1h': 3600, '4h': 14400, '1d': 86400 }

export function buildVolumeLookup(
  history: ScreenerVolumeDataPoint[] | undefined,
  volumeInterval: string | undefined,
  chartInterval: string,
): Map<number, ScreenerVolumeDataPoint> {
  const result = new Map<number, ScreenerVolumeDataPoint>()
  const step = INTERVAL_SECONDS[chartInterval]
  if (!step || volumeInterval !== chartInterval) return result
  for (const point of history || []) {
    if (!Number.isFinite(point.timestamp) || point.timestamp % step !== 0) continue
    const previous = result.get(point.timestamp)
    if (!previous || (point.updatedAt ?? 0) >= (previous.updatedAt ?? 0)) result.set(point.timestamp, point)
  }
  return result
}

export function volumeAtTime(
  lookup: Map<number, ScreenerVolumeDataPoint>,
  timeMs: number,
  interval: string,
): ScreenerVolumeDataPoint | null {
  const step = INTERVAL_SECONDS[interval]
  if (!step || !Number.isFinite(timeMs)) return null
  // Exact bucket lookup: never borrow adjacent volume or spread a minute over twelve 5s points.
  return lookup.get(Math.floor(timeMs / 1000 / step) * step) ?? null
}

function fullVolume(value: number | null | undefined): string | null {
  return value == null || !Number.isFinite(value) || value < 0 ? null
    : value.toLocaleString('en-US', { maximumFractionDigits: 8 })
}

export function volumeTooltipRow(point: ScreenerVolumeDataPoint | null, interval: string): string {
  if (!FEATURE_SCREENER_TRADES_ENABLED) return ''
  const label = INTERVAL_SECONDS[interval] ? interval : 'interval'
  const coins = fullVolume(point?.volumeCoins)
  const currency = point?.quoteCurrency?.trim().toUpperCase()
  const safeCurrency = currency && /^[A-Z0-9]{2,12}$/.test(currency) ? currency : null
  const nativeQuote = fullVolume(point?.volumeQuote)
  const usdt = fullVolume(point?.volumeUsdt)
  const legacyUsdt = point?.volumeQuote === undefined && point?.quoteCurrency === undefined
  const quote = safeCurrency && nativeQuote != null ? `${nativeQuote} ${safeCurrency}`
    : legacyUsdt && usdt != null ? `${usdt} USDT` : null
  const values = [quote, coins == null ? null : `${coins} coins`].filter(Boolean)
  const value = values.length > 0 ? values.join(' · ') : 'N/A'
  const partial = values.length > 0 && point?.complete === false
    ? ' <span style="color:#f59e0b;font-size:10px">(partial)</span>' : ''
  return `<div style="color:#94a3b8">Volume (${label}): <b style="color:#e2e8f0">${value}</b>${partial}</div>`
}
